import catalogData from '../data/policyInventoryCatalog.json'
import { REGULATORY_FRAMEWORK_OPTIONS } from '../data/industries'

export const SIZE_TARGETS = {
  SMB: { min: 10, max: 15, label: 'Streamlined' },
  'Mid-market': { min: 15, max: 25, label: 'Comprehensive' },
  Enterprise: { min: 25, max: 40, label: 'Full Coverage' }
}

const SIZE_ORDER = { SMB: 0, 'Mid-market': 1, Enterprise: 2 }

// Build bidirectional map between value keys (e.g., 'glba') and display labels (e.g., 'GLBA')
// so we can match client.regulatory_frameworks (value keys) against catalog required_by (labels)
const FRAMEWORK_VALUE_TO_LABEL = Object.fromEntries(
  REGULATORY_FRAMEWORK_OPTIONS.map(f => [f.value, f.label])
)
const FRAMEWORK_LABEL_TO_VALUE = Object.fromEntries(
  REGULATORY_FRAMEWORK_OPTIONS.map(f => [f.label, f.value])
)

/**
 * Expand framework value keys to include their display labels and vice versa.
 * Returns a Set that matches against either format.
 */
function expandFrameworks(frameworks) {
  const expanded = new Set()
  for (const f of frameworks) {
    expanded.add(f)
    // If it's a value key, also add the label
    if (FRAMEWORK_VALUE_TO_LABEL[f]) expanded.add(FRAMEWORK_VALUE_TO_LABEL[f])
    // If it's a label, also add the value key
    if (FRAMEWORK_LABEL_TO_VALUE[f]) expanded.add(FRAMEWORK_LABEL_TO_VALUE[f])
  }
  return expanded
}

/**
 * Derive organization size category from staffing and revenue.
 * @param {string} staffingSize - '1-10','11-50','51-200','201-500','500+'
 * @param {string} annualRevenue - 'Under $1M','$1M-$10M','$10M-$50M','$50M-$500M','$500M+'
 * @returns {'SMB'|'Mid-market'|'Enterprise'}
 */
export function deriveOrgSize(staffingSize, annualRevenue) {
  const staffMap = {
    '1-10': 'SMB',
    '11-50': 'SMB',
    '51-200': 'Mid-market',
    '201-500': 'Mid-market',
    '500+': 'Enterprise'
  }

  const revMap = {
    'Under $1M': 'SMB',
    '$1M-$10M': 'SMB',
    '$10M-$50M': 'Mid-market',
    '$50M-$500M': 'Mid-market',
    '$500M+': 'Enterprise'
  }

  const staffSize = staffMap[staffingSize] || 'SMB'
  const revSize = revMap[annualRevenue] || 'SMB'

  // Use the LARGER category (conservative)
  return (SIZE_ORDER[staffSize] || 0) >= (SIZE_ORDER[revSize] || 0) ? staffSize : revSize
}

/**
 * Calculate recommended policies based on client profile.
 * @param {Object} profile
 * @param {string} profile.industry
 * @param {string} profile.size - from deriveOrgSize()
 * @param {string[]} profile.frameworks - from clients.regulatory_frameworks
 * @returns {Object}
 */
export function calculateRecommendedPolicies(profile) {
  const { industry, size, frameworks = [] } = profile
  const sizeLevel = SIZE_ORDER[size] ?? 0
  const expandedFrameworks = expandFrameworks(frameworks)

  // 1. Start with base policies
  const selectedIds = new Set()
  const catalog = catalogData.map(p => ({ ...p }))

  for (const policy of catalog) {
    if (policy.is_base_policy) {
      selectedIds.add(policy.id)
    }
  }

  // 2. Add framework-triggered policies (deduplicated)
  // Catalog required_by uses display labels (e.g., "GLBA"), client frameworks use value keys (e.g., "glba")
  // expandedFrameworks contains both formats for reliable matching
  for (const policy of catalog) {
    if (!selectedIds.has(policy.id) && policy.required_by?.length > 0) {
      const hasMatchingFramework = policy.required_by.some(f => expandedFrameworks.has(f))
      if (hasMatchingFramework) {
        selectedIds.add(policy.id)
      }
    }
  }

  // 3. Remove policies not applicable to this industry (if industry filter is set)
  for (const policy of catalog) {
    if (
      selectedIds.has(policy.id) &&
      policy.applicable_industries?.length > 0 &&
      industry &&
      !policy.applicable_industries.includes(industry)
    ) {
      selectedIds.delete(policy.id)
    }
  }

  // 4. Remove policies not applicable to this size
  for (const policy of catalog) {
    if (
      selectedIds.has(policy.id) &&
      policy.applicable_sizes?.length > 0 &&
      !policy.applicable_sizes.includes(size)
    ) {
      selectedIds.delete(policy.id)
    }
  }

  // Build selected policies list
  let policies = catalog
    .filter(p => selectedIds.has(p.id))
    .map(p => ({
      id: p.id,
      name: p.name,
      domain: p.domain,
      category: p.category,
      description: p.description,
      isBase: p.is_base_policy,
      requiredBy: p.required_by || [],
      consolidated: false,
      consolidatedInto: null,
      consolidates: []
    }))

  // 5. Apply consolidation
  const consolidationThresholdLevel = { SMB: 0, 'Mid-market': 1, Enterprise: 2 }
  const policyMap = new Map(policies.map(p => [p.id, p]))
  const catalogMap = new Map(catalog.map(p => [p.id, p]))

  for (const policy of policies) {
    const catalogEntry = catalogMap.get(policy.id)
    if (
      !catalogEntry?.can_consolidate_with?.length ||
      !catalogEntry.consolidation_threshold
    ) continue

    const thresholdLevel = consolidationThresholdLevel[catalogEntry.consolidation_threshold] ?? -1
    if (sizeLevel > thresholdLevel) continue // larger orgs don't consolidate

    for (const parentId of catalogEntry.can_consolidate_with) {
      const parent = policyMap.get(parentId)
      if (parent && !parent.consolidated) {
        policy.consolidated = true
        policy.consolidatedInto = parentId
        if (!parent.consolidates) parent.consolidates = []
        parent.consolidates.push(policy.id)
        break
      }
    }
  }

  // 6. Sort by sort_order → category → name
  const sortOrderMap = new Map(catalog.map(p => [p.id, p.sort_order]))
  policies.sort((a, b) => {
    const orderDiff = (sortOrderMap.get(a.id) || 0) - (sortOrderMap.get(b.id) || 0)
    if (orderDiff !== 0) return orderDiff
    const catDiff = a.category.localeCompare(b.category)
    if (catDiff !== 0) return catDiff
    return a.name.localeCompare(b.name)
  })

  // Compute stats
  const totalConsolidated = policies.filter(p => p.consolidated).length
  const totalRecommended = policies.length - totalConsolidated
  const totalBase = policies.filter(p => p.isBase).length
  const totalOverlay = policies.filter(p => !p.isBase).length

  const consolidationSummary = policies
    .filter(p => p.consolidated && p.consolidatedInto)
    .map(p => {
      const parent = policyMap.get(p.consolidatedInto)
      return `${p.name} → merged into ${parent?.name || p.consolidatedInto}`
    })

  return {
    policies,
    totalRecommended,
    totalBase,
    totalOverlay,
    totalConsolidated,
    consolidationSummary,
    sizeCategory: size,
    sizeTargetRange: SIZE_TARGETS[size] || SIZE_TARGETS.SMB
  }
}

/**
 * Match catalog entries to existing policy_templates by domain + framework overlap.
 * @param {Object[]} catalogPolicies - from calculateRecommendedPolicies().policies
 * @param {Object[]} templates - policy_templates rows
 * @returns {Map<string, Object|null>} catalogId → matched template or null
 */
export function matchCatalogToTemplates(catalogPolicies, templates) {
  const matches = new Map()

  for (const policy of catalogPolicies) {
    // Find templates with matching domain
    const domainMatches = templates.filter(t => t.domain === policy.domain)

    if (domainMatches.length === 0) {
      matches.set(policy.id, null)
      continue
    }

    if (domainMatches.length === 1) {
      matches.set(policy.id, domainMatches[0])
      continue
    }

    // Multiple matches — prefer the one with the most framework_tags overlap
    let bestMatch = domainMatches[0]
    let bestOverlap = 0

    for (const template of domainMatches) {
      const overlap = (template.framework_tags || []).filter(
        tag => policy.requiredBy.includes(tag)
      ).length
      if (overlap > bestOverlap) {
        bestOverlap = overlap
        bestMatch = template
      }
    }

    matches.set(policy.id, bestMatch)
  }

  return matches
}
